import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

const MIN_VISIBLE_RATIO = 0.6;
const OBSERVER_THRESHOLDS = [0, 0.25, 0.5, 0.6, 0.75, 1];

type Ctx = {
  activeId: string | null;
  report: (id: string, ratio: number) => void;
  unregister: (id: string) => void;
  unmuted: boolean;
  setUnmuted: (v: boolean) => void;
  suspended: boolean;
  setSuspended: (v: boolean) => void;
};

const VideoVisibilityContext = createContext<Ctx | null>(null);

export function VideoVisibilityProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const ratios = useRef<Map<string, number>>(new Map());
  const [activeId, setActiveId] = useState<string | null>(null);
  const [unmuted, setUnmuted] = useState<boolean>(false);
  const [suspended, setSuspended] = useState<boolean>(false);

  const recompute = useCallback(() => {
    let bestId: string | null = null;
    let bestRatio = MIN_VISIBLE_RATIO;
    for (const [id, ratio] of ratios.current) {
      if (ratio >= bestRatio) {
        bestRatio = ratio;
        bestId = id;
      }
    }
    setActiveId((prev) => (prev === bestId ? prev : bestId));
  }, []);

  const report = useCallback(
    (id: string, ratio: number) => {
      ratios.current.set(id, ratio);
      recompute();
    },
    [recompute],
  );

  const unregister = useCallback(
    (id: string) => {
      ratios.current.delete(id);
      recompute();
    },
    [recompute],
  );

  const value = useMemo<Ctx>(
    () => ({
      activeId,
      report,
      unregister,
      unmuted,
      setUnmuted,
      suspended,
      setSuspended,
    }),
    [activeId, report, unregister, unmuted, suspended],
  );

  return (
    <VideoVisibilityContext.Provider value={value}>
      {children}
    </VideoVisibilityContext.Provider>
  );
}

export function useVideoVisibility(
  id: string,
  elementRef: React.RefObject<HTMLElement | null>,
): boolean {
  const ctx = useContext(VideoVisibilityContext);

  // Keep callbacks in a ref so this effect doesn't re-run every time
  // `activeId` changes (which would otherwise tear down and recreate the
  // IntersectionObserver on every visibility update and never let it settle).
  const callbacksRef = useRef<{
    report?: Ctx["report"];
    unregister?: Ctx["unregister"];
  }>({});
  callbacksRef.current = {
    report: ctx?.report,
    unregister: ctx?.unregister,
  };

  const enabled = !!ctx;

  useEffect(() => {
    if (!enabled) return;
    const el = elementRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        callbacksRef.current.report?.(id, entry.intersectionRatio);
      },
      { threshold: OBSERVER_THRESHOLDS },
    );
    observer.observe(el);

    return () => {
      observer.disconnect();
      callbacksRef.current.unregister?.(id);
    };
  }, [id, enabled, elementRef]);

  return !ctx?.suspended && ctx?.activeId === id;
}

export function useFeedUnmuted(): readonly [boolean, (v: boolean) => void] {
  const ctx = useContext(VideoVisibilityContext);
  // Always call useState so hook order stays stable across renders. When a
  // provider is present we hand back its shared state instead.
  const [local, setLocal] = useState<boolean>(false);
  if (ctx) return [ctx.unmuted, ctx.setUnmuted] as const;
  return [local, setLocal] as const;
}

export function useFeedVideosSuspended(): readonly [
  boolean,
  (v: boolean) => void,
] {
  const ctx = useContext(VideoVisibilityContext);
  const [local, setLocal] = useState<boolean>(false);
  if (ctx) return [ctx.suspended, ctx.setSuspended] as const;
  return [local, setLocal] as const;
}
